import * as React from "react";
import { useState } from "react";
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Toast,
  ToastTitle,
  ToastBody,
  useToastController,
} from "@fluentui/react-components";
import {
  Settings20Regular,
  SignOut20Regular,
  ArrowLeft20Regular,
  Person20Regular,
} from "@fluentui/react-icons";
import { useAuth } from "../features/auth/useAuth";
import { describeAuthError } from "../features/auth/authDiagnostics";
import SignInScreen from "../features/auth/SignInScreen";
import SettingsPanel from "../features/settings/SettingsPanel";
import SendSmsPanel from "../features/manual-sms/SendSmsPanel";
import { loadSettings, clearSettings } from "../features/settings/settingsStorage";
import { clearLastSyncedAt } from "../features/settings/settingsSyncApi";
import { disableCalendarAutomation } from "../features/calendar/calendarAutomationApi";
import { TOASTER_ID } from "../toaster";
import { useTranslation } from "react-i18next";

type View = "settings" | "main";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "6px 8px 6px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  topBarLeft: { display: "flex", alignItems: "center", gap: "4px", minWidth: 0 },
  title: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  body: { flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" },
  avatarButton: {
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    borderRadius: "50%",
    lineHeight: 0,
  },
  menuHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 10px 8px",
    maxWidth: "260px",
  },
  email: { wordBreak: "break-all" },
});

interface AccountMenuProps {
  username?: string;
  /** Sign-out is tearing down server-side automation; it takes a moment. */
  signingOut?: boolean;
  onSettings: () => void;
  onSignOut: () => void;
}

const AccountMenu: React.FC<AccountMenuProps> = ({
  username,
  signingOut,
  onSettings,
  onSignOut,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <button className={styles.avatarButton} aria-label={t("account.menu")}>
          <Avatar icon={<Person20Regular />} color="brand" size={28} />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <div className={styles.menuHeader}>
          <Avatar icon={<Person20Regular />} color="brand" size={36} />
          <Text size={200} weight="semibold" className={styles.email}>
            {username ?? t("account.signedIn")}
          </Text>
        </div>
        <MenuList>
          <MenuItem icon={<Settings20Regular />} onClick={onSettings}>
            {t("account.settings")}
          </MenuItem>
          <MenuItem icon={<SignOut20Regular />} disabled={signingOut} onClick={onSignOut}>
            {signingOut ? t("account.signingOut") : t("account.signOut")}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

interface AuthenticatedAppProps {
  username?: string;
  /** Graph token for the live session, handed to features that call Graph. */
  graphToken?: string;
  onSignOut: () => void;
}

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = ({ username, graphToken, onSignOut }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [configured, setConfigured] = useState(() => loadSettings().apiKey.trim() !== "");
  const [view, setView] = useState<View>(configured ? "main" : "settings");
  const [signingOut, setSigningOut] = useState(false);
  const { dispatchToast } = useToastController(TOASTER_ID);

  const notifySignOutFailure = (detail: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{t("account.signOutFailed")}</ToastTitle>
        <ToastBody>{t("account.signOutFailedBody", { detail })}</ToastBody>
      </Toast>,
      { intent: "error" }
    );

  const handleSaved = () => {
    setConfigured(true);
    setView("main");
  };

  // Sign-out has to tear down the server side first (REQ-AUTH-009). Clearing
  // local settings wipes the automation flag, so a user who signed out with it
  // enabled would be left with a live Graph subscription and a stored API key
  // and no way to reach either. A failure here blocks sign-out rather than
  // silently orphaning credentials.
  const handleSignOut = async () => {
    if (loadSettings().calendarAutomation) {
      setSigningOut(true);
      try {
        await disableCalendarAutomation();
        clearLastSyncedAt();
      } catch (e) {
        setSigningOut(false);
        notifySignOutFailure(describeAuthError(e));
        return;
      }
      setSigningOut(false);
    }
    try {
      await clearSettings();
    } catch {}
    onSignOut();
  };

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          {view === "settings" && configured && (
            <Button
              appearance="subtle"
              icon={<ArrowLeft20Regular />}
              aria-label={t("common.back")}
              onClick={() => setView("main")}
            />
          )}
          {view === "settings" && <Text className={styles.title}>{t("account.settings")}</Text>}
        </div>
        <AccountMenu
          username={username}
          signingOut={signingOut}
          onSettings={() => setView("settings")}
          onSignOut={handleSignOut}
        />
      </div>

      <div className={styles.body}>
        {view === "settings" ? (
          <SettingsPanel onSaved={handleSaved} />
        ) : (
          <SendSmsPanel graphToken={graphToken} />
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const auth = useAuth();

  if (auth.status !== "signedIn") {
    return (
      <SignInScreen
        status={auth.status}
        error={auth.error}
        diagnostic={auth.diagnostic}
        onSignIn={auth.signIn}
      />
    );
  }

  return (
    <AuthenticatedApp username={auth.username} graphToken={auth.token} onSignOut={auth.signOut} />
  );
};

export default App;
